import React, { useMemo } from 'react';
import { Button } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { putOrder, ordersSelectors } from '../../features/orders';
import { cartSelectors } from '../../features/cart';
import { addressSelectors, ADDRESS_FIELD_NAMES } from '../../features/address';
import { shippingSelectors } from '../../features/shipping';
import { promoSelectors } from '../../features/promo';
import { bonusesSelectors } from '../../features/bonuses';
import { selectUser } from '../../features/auth/selectors';

const SHIPPING_TYPE_LABELS = {
    SHIPPING_TYPE_LOCAL_PICKUP: 'самовывоз',
    SHIPPING_TYPE_DELIVERING: 'доставка курьером',
};

export default function ConfirmOrderButton(props) {
    const { paymentMethod } = props;
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const {
        FIRSTNAME,
        LASTNAME,
        PHONE,
        EMAIL,
        IS_JURIDIC,
        COMPANY,
        COMPANY_INN,
        COMPANY_KPP,
        COMPANY_OGRN,
        COMPANY_CHIEF_NAME,
        COMPANY_CONTACT_NAME,
        COMPANY_JOB_TITLE,
        COMPANY_SCOPE,
        COMPANY_LEGAL_ADDR,
        COMPANY_POST_ADDR,
    } = ADDRESS_FIELD_NAMES;

    const cartItems = useSelector(cartSelectors.selectCartItemsForServer);
    const fields = useSelector(addressSelectors.selectAddressFields);
    const shippingAddress = useSelector(shippingSelectors.selectAddress);
    const selectedOption = useSelector(shippingSelectors.selectSelectedOption);
    const shippingCost = useSelector(shippingSelectors.selectSelectedShippingCost);
    const deliveryDays = useSelector(shippingSelectors.selectSelectedDeliveryDays);
    const shippingOptions = useSelector(shippingSelectors.selectShippingOptions);
    const selectedPickupPoint = useSelector(shippingSelectors.selectSelectedPickupPoint);
    const isPickupSelected = useSelector(shippingSelectors.selectIsPickupSelected);
    const promoCoupon = useSelector(promoSelectors.selectCoupon);
    const bonusCoupon = useSelector(bonusesSelectors.selectBonusCoupon);
    const isOrderLoading = useSelector(ordersSelectors.selectIsPutOrderLoading);
    const authUser = useSelector(selectUser);
    const allVirtual = useSelector(cartSelectors.selectAllItemsVirtual);

    const validation = useMemo(() => {
        const errors = [];

        if (!cartItems || cartItems.length === 0) {
            errors.push('Корзина пуста');
        }

        if (!fields?.[FIRSTNAME]?.trim()) {
            errors.push('Укажите имя');
        }
        if (!fields?.[LASTNAME]?.trim()) {
            errors.push('Укажите фамилию');
        }
        if (!fields?.[PHONE]?.trim()) {
            errors.push('Укажите телефон');
        }

        if (!shippingAddress?.fullAddress) {
            errors.push('Укажите адрес');
        }

        if (!allVirtual) {
            if (!selectedOption) {
                errors.push('Выберите способ доставки');
            }

            if (isPickupSelected && !selectedPickupPoint) {
                errors.push('Выберите пункт выдачи на карте');
            }
        }

        if (!paymentMethod) {
            errors.push('Выберите способ оплаты');
        }

        return {
            isValid: errors.length === 0,
            errors,
        };
    }, [cartItems, fields, shippingAddress, selectedOption, paymentMethod, isPickupSelected, selectedPickupPoint, allVirtual, FIRSTNAME, LASTNAME, PHONE]);

    const shippingData = useMemo(() => {
        if (!selectedOption || !shippingOptions) return null;
        return shippingOptions[selectedOption] || null;
    }, [selectedOption, shippingOptions]);

    const getShippingInfo = () => {
        if (allVirtual) {
            return {
                shipping_method: 'Самовывоз',
                shipping_cost: 0,
                shipping_method_id: 'own::SHIPPING_TYPE_LOCAL_PICKUP',
                shipping_instance_id: '0',
            };
        }

        if (!selectedOption) return {};

        const [method, type] = selectedOption.split('::');

        const methodNames = {
            cdek: 'СДЭК',
            own: 'e-Feed',
            dellin: 'Деловые линии',
        };

        const typeLabels = {
            SHIPPING_TYPE_LOCAL_PICKUP: 'пункт выдачи',
            SHIPPING_TYPE_DELIVERING: 'доставка курьером',
        };

        const isOwnPickup = method === 'own' && type === 'SHIPPING_TYPE_LOCAL_PICKUP';
        const methodName = methodNames[method] || method;
        const typeLabel = typeLabels[type] || type;
        const shippingMethodLabel = isOwnPickup
            ? 'Самовывоз'
            : `${methodName} ${typeLabel} за ${deliveryDays || '?'} дн.`;

        return {
            shipping_method: shippingMethodLabel,
            shipping_cost: shippingCost || 0,
            shipping_method_id: selectedOption,
            shipping_instance_id: '0',
        };
    };

    const getMetaInfo = () => {
        if (allVirtual) {
            return {
                tracking_info: {
                    company: 'e-Feed пункт выдачи',
                    tracking_link: '',
                    track_code: '',
                    estimated_date_arrival: '',
                    shipping_data: {
                        data: { shipping_details: { total_cost: 0, delivery_days: 0, details: [] } },
                        shipping_type: 'SHIPPING_TYPE_LOCAL_PICKUP',
                        shipping_method: 'own',
                    },
                },
            };
        }

        if (!selectedOption) return {};

        const [method, type] = selectedOption.split('::');

        const meta = {
            tracking_info: {
                company: getShippingInfo().shipping_method || '',
                tracking_link: '',
                track_code: '',
                estimated_date_arrival: '',
                shipping_data: {
                    data: shippingData?.data || {
                        shipping_details: {
                            total_cost: shippingCost || 0,
                            delivery_days: deliveryDays || 0,
                            details: [],
                        },
                    },
                    shipping_type: type || '',
                    shipping_method: method || '',
                },
            },
        };

        if (selectedPickupPoint) {
            meta.pickup_point = {
                id: selectedPickupPoint.id || '',
                company: selectedPickupPoint.company || '',
                address: selectedPickupPoint.address || '',
                amount: selectedPickupPoint.amount || 0,
            };
        }

        return meta;
    };

    const getBillingData = () => {
        const billing = {
            first_name: fields[FIRSTNAME] || '',
            last_name: fields[LASTNAME] || '',
            email: fields[EMAIL] || '',
            phone: fields[PHONE] || '',
            address_1: shippingAddress?.fullAddress || '',
            city: shippingAddress?.city || '',
            state: shippingAddress?.region || '',
            postcode: shippingAddress?.postalCode || '',
            country: 'RU',
        };

        if (fields[IS_JURIDIC]) {
            billing.is_juridic = 1;
            billing.company = fields[COMPANY] || '';
            billing.company_inn = fields[COMPANY_INN] || '';
            billing.company_kpp = fields[COMPANY_KPP] || '';
            billing.company_ogrn = fields[COMPANY_OGRN] || '';
            billing.company_chief_name = fields[COMPANY_CHIEF_NAME] || '';
            billing.company_contact_name = fields[COMPANY_CONTACT_NAME] || '';
            billing.company_job_title = fields[COMPANY_JOB_TITLE] || '';
            billing.company_scope = fields[COMPANY_SCOPE] || '';
            billing.company_legal_address = fields[COMPANY_LEGAL_ADDR] || '';
            billing.company_post_address = fields[COMPANY_POST_ADDR] || '';
        }

        return billing;
    };

    const handleClick = () => {
        if (!validation.isValid) {
            return;
        }

        const orderCartItems = cartItems.map(item => ({
            id: item.id,
            variationId: item.virtual ? 0 : (item.variationId ? Number(item.variationId) || 0 : 0),
            quantity: item.quantity,
        }));

        const orderData = {
            existing_user_id: authUser?.id || 0,
            create_account: 0,
            coupon: bonusCoupon || promoCoupon || '',
            payment_option: paymentMethod || '',
            cart_items: orderCartItems,
            billing: getBillingData(),
            shipping: getShippingInfo(),
            meta: getMetaInfo(),
            user_location: {
                lat: shippingAddress?.geoLat || null,
                lng: shippingAddress?.geoLon || null,
            },
            success_url: `${window.location.origin}/order-success`,
            fail_url: `${window.location.origin}/order-success`,
        };

        const onSuccess = (orderResult) => {
            navigate(`/order-success/${orderResult.id}`);
        };

        dispatch(putOrder(orderData, onSuccess));
    };

    const tooltipText = !validation.isValid
        ? validation.errors.join(', ')
        : '';

    return (
        <Button
            className={props.addClassName}
            onClick={handleClick}
            loading={isOrderLoading}
            disabled={props.disabled || isOrderLoading || !validation.isValid}
            title={tooltipText}
            block
            type="primary"
        >
            {props.buttonText || 'Подтвердить заказ'}
        </Button>
    );
}
